/**
 * Google Maps Places UI Kit Block
 *
 * Renders a 3D map with:
 * - Property's own curated POIs from neighborhood_pois (with Google Place ID popovers)
 * - Nearby place search powered by the Places UI Kit
 * - The property's own Google Place details card
 * - Walk/transit score badges
 * - Auto-themed via the site's design tokens
 *
 * All location data defaults to the property's address/place fields.
 */

import { useRef, useEffect, useCallback, useState } from 'react';
import type { BlockDefinition, BlockProps } from '../../types';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useBlockToken, EditableBlockToken } from '@vibesites/runtime/react';
import { useProperty, useNeighborhoodPOIs } from '@vibesites/runtime/react';
import { defineComponentRequirements, collections } from '@vibesites/tokens';
import type { NeighborhoodPOI } from '@vibesites/schema';

// ============================================================================
// Google Maps bootstrap loader
// ============================================================================

const MAPS_SCRIPT_ID = '__gmp_places_ui_kit';

let loaderPromise: Promise<void> | null = null;

function loadGoogleMapsApi(apiKey: string): Promise<void> {
  if (loaderPromise) return loaderPromise;

  loaderPromise = new Promise<void>((resolve, reject) => {
    if (document.getElementById(MAPS_SCRIPT_ID)) {
      resolve();
      return;
    }

    const g: Record<string, unknown> = { key: apiKey, v: 'beta' };
    const w = window as any;
    const google = (w.google = w.google || {});
    const maps = (google.maps = google.maps || {});
    const waiting = new Set<string>();

    const flush = () =>
      loaderPromise!.then(() => maps.importLibrary as Function);

    if (!maps.importLibrary) {
      maps.importLibrary = (lib: string, ...rest: unknown[]) => {
        waiting.add(lib);
        return flush().then((fn: Function) => fn(lib, ...rest));
      };
    }

    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(g)) {
      params.set(k.replace(/[A-Z]/g, (ch) => '_' + ch.toLowerCase()), String(v));
    }
    params.set('libraries', [...waiting].join(','));
    params.set('callback', 'google.maps.__ib__');

    maps.__ib__ = resolve;

    const script = document.createElement('script');
    script.id = MAPS_SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?${params}`;
    script.onerror = () => {
      loaderPromise = null;
      reject(new Error('Google Maps API could not load.'));
    };
    script.nonce =
      document.querySelector<HTMLScriptElement>('script[nonce]')?.nonce || '';
    document.head.appendChild(script);
  });

  return loaderPromise;
}

// ============================================================================
// Helpers
// ============================================================================

const DEFAULT_API_KEY =
  (typeof import.meta !== 'undefined' &&
    (import.meta as any).env?.GOOGLE_MAPS_API_KEY) ||
  '';

function extractPlaceId(url: string | null | undefined): string | null {
  if (!url) return null;
  const match = url.match(/place_id[=:]([A-Za-z0-9_-]+)/);
  return match?.[1] ?? null;
}

// ============================================================================
// Score Badge sub-component
// ============================================================================

function ScoreBadge({ label, value }: { label: string; value: string }) {
  const num = parseInt(value, 10);
  if (isNaN(num)) return null;

  let color = 'var(--vibe-color-text-muted)';
  if (num >= 70) color = '#22c55e';
  else if (num >= 50) color = '#eab308';
  else color = '#ef4444';

  return (
    <div
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium backdrop-blur-sm"
      style={{ background: 'rgba(0,0,0,0.6)', color: '#fff' }}
    >
      <span
        className="inline-block w-2.5 h-2.5 rounded-full"
        style={{ background: color }}
      />
      <span>{label}</span>
      <span className="font-bold">{num}</span>
    </div>
  );
}

// ============================================================================
// Component
// ============================================================================

function GoogleMapsPlacesComponent({ sectionId: _sectionId }: BlockProps) {
  const property = useProperty();
  const pois = useNeighborhoodPOIs();
  const propertyPlaceId = extractPlaceId(property?.property_google_place_url);

  // --- tokens ---
  const apiKey = useBlockToken('api_key', { defaultValue: DEFAULT_API_KEY });
  const placeIdToken = useBlockToken('place_id', {
    defaultField: 'property_google_place_url',
    defaultValue: '',
  });
  const latToken = useBlockToken('latitude', {
    defaultField: 'property_address_latitude',
    defaultValue: '',
  });
  const lonToken = useBlockToken('longitude', {
    defaultField: 'property_address_longitude',
    defaultValue: '',
  });
  const searchRadius = useBlockToken('search_radius', { defaultValue: '1500' });
  const placeTypes = useBlockToken('place_types', { defaultValue: 'restaurant' });
  const maxResults = useBlockToken('max_results', { defaultValue: '5' });
  const mapMode = useBlockToken('map_mode', { defaultValue: 'hybrid' });
  const range = useBlockToken('range', { defaultValue: '5000' });
  const tilt = useBlockToken('tilt', { defaultValue: '45' });
  const heading = useBlockToken('heading', { defaultValue: '0' });
  const height = useBlockToken('height', { defaultValue: '600' });
  const showPropertyCard = useBlockToken('show_property_card', { defaultValue: 'true' });
  const showNearbySearch = useBlockToken('show_nearby_search', { defaultValue: 'true' });
  const showPOIs = useBlockToken('show_pois', { defaultValue: 'true' });
  const showScores = useBlockToken('show_scores', { defaultValue: 'true' });
  const popoverContent = useBlockToken('popover_content', { defaultValue: 'standard' });

  // --- derived values ---
  const resolvedPlaceId =
    extractPlaceId(placeIdToken) || placeIdToken || propertyPlaceId || null;

  const lat = latToken || property?.property_address_latitude || '';
  const lon = lonToken || property?.property_address_longitude || '';
  const center = lat && lon ? `${lat},${lon}` : '21.27655,-157.82663';

  const walkScore = property?.property_walk_score || '';
  const transitScore = property?.property_transit_score || '';
  const scoresVisible = showScores !== 'false' && (walkScore || transitScore);

  const validPois = showPOIs !== 'false'
    ? (pois || []).filter((p): p is NeighborhoodPOI =>
        p != null && !!(p.poi_latitude && p.poi_longitude))
    : [];

  const containerRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  // --- build the Google Maps DOM ---
  const buildUI = useCallback(async () => {
    if (!apiKey || !containerRef.current) return;

    try {
      await loadGoogleMapsApi(apiKey);
      // @ts-ignore – google is a runtime global injected by the Maps bootstrap script
      await google.maps.importLibrary('places');
      // @ts-ignore
      const maps3d = (await google.maps.importLibrary('maps3d')) as any;
      const { Marker3DInteractiveElement, AltitudeMode } = maps3d;
      // @ts-ignore
      const { PinElement } = (await google.maps.importLibrary('marker')) as any;

      const container = containerRef.current;
      if (!container) return;
      container.innerHTML = '';

      // Resolve mode — gmp-map-3d only accepts "satellite" or "hybrid"
      const resolvedMode = mapMode === 'satellite' ? 'satellite' : 'hybrid';

      // --- gmp-map-3d ---
      const map3d = document.createElement('gmp-map-3d') as any;
      map3d.setAttribute('center', center);
      map3d.setAttribute('mode', resolvedMode);
      map3d.setAttribute('range', range);
      map3d.setAttribute('tilt', tilt);
      map3d.setAttribute('heading', heading);
      map3d.style.width = '100%';
      map3d.style.height = '100%';

      // --- apply theme CSS vars ---
      const themeVars: Record<string, string> = {
        '--gmp-mat-color-primary': 'var(--vibe-color-primary)',
        '--gmp-mat-color-surface': 'var(--vibe-color-surface)',
        '--gmp-mat-color-on-surface': 'var(--vibe-color-text)',
        '--gmp-mat-color-on-surface-variant': 'var(--vibe-color-text-muted)',
        '--gmp-mat-font-family': 'var(--vibe-font-body)',
      };

      // --- popover for markers ---
      const popover = document.createElement('gmp-popover') as any;
      popover.style.setProperty('--gmp-popover-pixel-offset-y', '-48px');
      popover.setAttribute('altitude-mode', 'relative-to-mesh');

      const detailsCompact = document.createElement('gmp-place-details-compact') as any;
      detailsCompact.setAttribute('orientation', 'horizontal');
      detailsCompact.style.width = '350px';
      for (const [prop, val] of Object.entries(themeVars)) {
        detailsCompact.style.setProperty(prop, val);
      }

      const detailsRequest = document.createElement('gmp-place-details-place-request') as any;
      if (resolvedPlaceId) detailsRequest.place = resolvedPlaceId;

      // Popover content config
      if (popoverContent === 'all') {
        detailsCompact.appendChild(detailsRequest);
        detailsCompact.appendChild(document.createElement('gmp-place-all-content'));
      } else if (popoverContent === 'minimal') {
        detailsCompact.appendChild(detailsRequest);
        const cfg = document.createElement('gmp-place-content-config');
        cfg.appendChild(document.createElement('gmp-place-rating'));
        cfg.appendChild(document.createElement('gmp-place-type'));
        cfg.appendChild(document.createElement('gmp-place-open-now-status'));
        detailsCompact.appendChild(cfg);
      } else {
        detailsCompact.appendChild(detailsRequest);
        detailsCompact.appendChild(document.createElement('gmp-place-standard-content'));
      }

      popover.appendChild(detailsCompact);
      map3d.appendChild(popover);

      // --- helper: open popover for a place ---
      const handlePlaceClick = (placeId: string, location: any) => {
        detailsRequest.place = placeId;
        popover.positionAnchor = location;
        popover.open = true;
      };

      // --- property pin at center ---
      if (lat && lon) {
        const propertyPosition = { lat: parseFloat(lat), lng: parseFloat(lon) };
        if (!isNaN(propertyPosition.lat) && !isNaN(propertyPosition.lng)) {
          const propertyPin = new PinElement({
            scale: 1.8,
            background: 'var(--vibe-color-primary, #e63946)',
            borderColor: '#ffffff',
            glyphColor: '#ffffff',
          });

          const propertyMarker = new Marker3DInteractiveElement({
            position: propertyPosition,
            extruded: true,
            drawsWhenOccluded: true,
            altitudeMode: AltitudeMode.RELATIVE_TO_MESH,
          });
          propertyMarker.append(propertyPin);

          if (resolvedPlaceId) {
            propertyMarker.addEventListener('gmp-click', () =>
              handlePlaceClick(resolvedPlaceId, propertyPosition),
            );
          }

          map3d.append(propertyMarker);
        }
      }

      container.appendChild(map3d);

      // --- neighborhood POI markers ---
      if (validPois.length > 0) {
        for (const poi of validPois) {
          const position = {
            lat: parseFloat(poi.poi_latitude!),
            lng: parseFloat(poi.poi_longitude!),
          };
          if (isNaN(position.lat) || isNaN(position.lng)) continue;

          const marker = new Marker3DInteractiveElement({
            position,
            extruded: true,
            drawsWhenOccluded: true,
            altitudeMode: AltitudeMode.RELATIVE_TO_MESH,
          });

          if (poi.poi_google_place_id) {
            marker.addEventListener('gmp-click', () =>
              handlePlaceClick(poi.poi_google_place_id!, position),
            );
          }

          map3d.append(marker);
        }
      }

      // --- compact property card (positioned above the search panel) ---
      const hasPropertyCard = showPropertyCard !== 'false' && resolvedPlaceId;
      const hasNearbySearch = showNearbySearch !== 'false';

      if (hasPropertyCard) {
        const propDetails = document.createElement('gmp-place-details-compact') as any;
        propDetails.setAttribute('orientation', 'horizontal');
        propDetails.style.cssText = [
          'position: absolute',
          'inset: 16px auto auto 16px',
          'width: 400px',
          'max-width: calc(100% - 32px)',
          'z-index: 11',
        ].join(';');
        for (const [prop, val] of Object.entries(themeVars)) {
          propDetails.style.setProperty(prop, val);
        }

        const propRequest = document.createElement('gmp-place-details-place-request') as any;
        propRequest.place = resolvedPlaceId;
        propDetails.appendChild(propRequest);

        const propContent = document.createElement('gmp-place-content-config');
        propContent.appendChild(document.createElement('gmp-place-media'));
        propContent.appendChild(document.createElement('gmp-place-rating'));
        propContent.appendChild(document.createElement('gmp-place-type'));
        const attribution = document.createElement('gmp-place-attribution') as any;
        attribution.setAttribute('light-scheme-color', 'gray');
        attribution.setAttribute('dark-scheme-color', 'white');
        propContent.appendChild(attribution);
        propDetails.appendChild(propContent);

        container.appendChild(propDetails);

        // Once the property card renders, position the search panel below it
        propDetails.addEventListener('gmp-load', () => {
          const searchEl = container.querySelector('.gmp-search-panel') as HTMLElement;
          if (searchEl) {
            const cardBottom = propDetails.offsetTop + propDetails.offsetHeight + 8;
            searchEl.style.top = `${cardBottom}px`;
          }
        });
      }

      // --- nearby search panel (mirrors demo: gmp-place-search as direct sibling of map) ---
      if (hasNearbySearch) {
        const placeSearch = document.createElement('gmp-place-search') as any;
        placeSearch.setAttribute('selectable', '');
        placeSearch.className = 'gmp-search-panel';
        placeSearch.style.cssText = [
          'position: absolute',
          `inset: ${hasPropertyCard ? '100px' : '16px'} auto auto 16px`,
          'width: 400px',
          'max-width: calc(100% - 32px)',
          'z-index: 10',
        ].join(';');
        for (const [prop, val] of Object.entries(themeVars)) {
          placeSearch.style.setProperty(prop, val);
        }

        placeSearch.appendChild(document.createElement('gmp-place-all-content'));

        const nearbyRequest = document.createElement('gmp-place-nearby-search-request') as any;
        nearbyRequest.setAttribute('max-result-count', maxResults);
        placeSearch.appendChild(nearbyRequest);

        container.appendChild(placeSearch);

        // Wire up search result markers
        const searchMarkers: any[] = [];

        placeSearch.addEventListener('gmp-load', (e: any) => {
          for (const m of searchMarkers) m.remove();
          searchMarkers.length = 0;

          for (const place of e.target.places) {
            const marker = new Marker3DInteractiveElement({
              position: place.location,
              extruded: true,
              drawsWhenOccluded: true,
              altitudeMode: AltitudeMode.RELATIVE_TO_MESH,
            });
            marker.addEventListener('gmp-click', () =>
              handlePlaceClick(place.id, place.location),
            );
            map3d.append(marker);
            searchMarkers.push(marker);
          }
        });

        placeSearch.addEventListener('gmp-select', ({ place }: any) =>
          handlePlaceClick(place.id, place.location),
        );

        // Wait for the custom element to be defined, then set search properties via JS.
        customElements.whenDefined('gmp-place-nearby-search-request').then(() => {
          const centerLat = parseFloat(lat || '0');
          const centerLng = parseFloat(lon || '0');
          if (!isNaN(centerLat) && !isNaN(centerLng) && centerLat !== 0) {
            nearbyRequest.locationRestriction = {
              center: { lat: centerLat, lng: centerLng },
              radius: parseFloat(searchRadius || '1500') || 1500,
            };
          }
          const types = (placeTypes || 'restaurant').split(',').map((t: string) => t.trim()).filter(Boolean);
          if (types.length > 0) {
            nearbyRequest.includedPrimaryTypes = types;
          }
        });
      }

      setStatus('ready');
    } catch {
      setStatus('error');
    }
  }, [
    apiKey, center, searchRadius, placeTypes, maxResults,
    mapMode, range, tilt, heading, resolvedPlaceId, popoverContent,
    showPropertyCard, showNearbySearch, validPois.length,
  ]);

  useEffect(() => {
    if (!apiKey) return;
    setStatus('loading');
    buildUI();
  }, [buildUI, apiKey]);

  // --- empty / error states ---
  if (!apiKey) {
    return (
      <div
        className="flex items-center justify-center py-16 text-center"
        style={{ color: 'var(--vibe-color-text-muted)', background: 'var(--vibe-color-surface)' }}
      >
        <div>
          <EditableBlockToken
            localName="api_key"
            type="text"
            label="Google Maps API Key"
            as="p"
            className="text-lg font-medium"
            style={{ fontFamily: 'var(--vibe-font-heading)' }}
          >
            Google Maps Places
          </EditableBlockToken>
          <p className="text-sm mt-1">
            Set a Google Maps API key to display the Places UI Kit.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative w-full overflow-hidden"
      style={{
        height: `${height}px`,
        background: 'var(--vibe-color-surface)',
      }}
    >
      {status === 'loading' && (
        <div className="absolute inset-0 flex items-center justify-center z-20">
          <span
            className="text-sm animate-pulse"
            style={{ color: 'var(--vibe-color-text-muted)' }}
          >
            Loading map…
          </span>
        </div>
      )}
      {status === 'error' && (
        <div className="absolute inset-0 flex items-center justify-center z-20">
          <span className="text-sm text-red-500">
            Failed to load Google Maps. Check the API key and network.
          </span>
        </div>
      )}

      {/* Walk / Transit score badges */}
      {scoresVisible && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {walkScore && <ScoreBadge label="Walk Score" value={walkScore} />}
          {transitScore && <ScoreBadge label="Transit Score" value={transitScore} />}
        </div>
      )}

      <div ref={containerRef} className="w-full h-full" />
    </div>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

const contentSchema: ComponentContentSchema = {
  slot: 'GoogleMapsPlaces',
  displayName: 'Google Maps Places',
  tokens: {
    // --- API ---
    api_key: {
      label: 'Google Maps API Key',
      type: 'text',
      defaultValue: DEFAULT_API_KEY,
      description:
        'Your Google Maps API key. Defaults to the GOOGLE_MAPS_API_KEY environment variable.',
    },
    place_id: {
      label: 'Google Place ID or URL',
      type: 'text',
      defaultField: 'property_google_place_url',
      suggestedFields: ['property_google_place_url'],
      description:
        'A Google Place ID (e.g. ChIJ...) or a full Google Maps place URL. Defaults to the property\'s Google Place URL.',
    },

    // --- Location ---
    latitude: {
      label: 'Latitude',
      type: 'text',
      defaultField: 'property_address_latitude',
      suggestedFields: ['property_address_latitude'],
      description: 'Latitude for the map center. Defaults to the property address latitude.',
    },
    longitude: {
      label: 'Longitude',
      type: 'text',
      defaultField: 'property_address_longitude',
      suggestedFields: ['property_address_longitude'],
      description: 'Longitude for the map center. Defaults to the property address longitude.',
    },

    // --- Map appearance ---
    map_mode: {
      label: 'Map Mode',
      type: 'text',
      defaultValue: 'hybrid',
      description:
        'Map rendering mode. "hybrid" shows satellite imagery with road labels and overlays. "satellite" shows pure satellite imagery without labels.',
    },
    range: {
      label: 'Camera Distance (m)',
      type: 'text',
      defaultValue: '5000',
      description: 'Camera distance from the map center in meters. Higher values zoom out further. 2000 = close neighborhood, 5000 = wider area, 10000+ = city-level.',
    },
    tilt: {
      label: 'Map Tilt (°)',
      type: 'text',
      defaultValue: '45',
      description: 'Camera tilt angle. 0 = top-down (2D-like), 45 = moderate 3D perspective, 73+ = near-horizon cinematic view.',
    },
    heading: {
      label: 'Map Heading (°)',
      type: 'text',
      defaultValue: '0',
      description: 'Camera compass heading in degrees (0 = north, 90 = east, 180 = south, 270 = west).',
    },
    height: {
      label: 'Height (px)',
      type: 'text',
      defaultValue: '600',
      description: 'Container height in pixels.',
    },

    // --- Search ---
    search_radius: {
      label: 'Search Radius (m)',
      type: 'text',
      defaultValue: '1500',
      description: 'Radius in meters for the nearby place search and initial map range.',
    },
    place_types: {
      label: 'Place Types',
      type: 'text',
      defaultValue: 'restaurant',
      description:
        'Comma-separated Google Place types for the nearby search (e.g. restaurant, cafe, gym, grocery_or_supermarket, park, school, transit_station).',
    },
    max_results: {
      label: 'Max Results',
      type: 'text',
      defaultValue: '5',
      description: 'Maximum number of nearby search results to display.',
    },

    // --- Feature toggles ---
    show_property_card: {
      label: 'Show Property Card',
      type: 'text',
      defaultValue: 'true',
      description: 'Show the property\'s own Google Place details card (rating, hours, photos). Requires a valid Place ID.',
    },
    show_nearby_search: {
      label: 'Show Nearby Search',
      type: 'text',
      defaultValue: 'true',
      description: 'Show the nearby place search list panel.',
    },
    show_pois: {
      label: 'Show Neighborhood POIs',
      type: 'text',
      defaultValue: 'true',
      description: 'Plot the property\'s curated neighborhood POIs as markers on the map.',
    },
    show_scores: {
      label: 'Show Walk/Transit Scores',
      type: 'text',
      defaultValue: 'true',
      description: 'Display walk score and transit score badges over the map.',
    },
    popover_content: {
      label: 'Popover Detail Level',
      type: 'text',
      defaultValue: 'standard',
      description:
        'Controls how much detail is shown when clicking a marker. "standard" shows the default set. "all" shows everything. "minimal" shows only rating, type, and open status.',
    },
  },
};

// ============================================================================
// Block Definition
// ============================================================================

export const googleMapsPlacesBlock: BlockDefinition = {
  id: 'google-maps-places',
  name: 'Google Maps Places',
  description:
    'Interactive 3D map with the property\'s Google Place card, curated neighborhood POI markers, nearby place search, walk/transit scores, and auto-themed styling — powered by the Google Maps Places UI Kit.',
  category: 'Embeds',
  component: GoogleMapsPlacesComponent,
  contentSchema,
  requirements: defineComponentRequirements('GoogleMapsPlaces', {
    designTokens: [
      'color.primary',
      'color.surface',
      'color.text',
      'color.textMuted',
      'font.body',
      'font.heading',
    ],
    dataTokens: [
      collections.optional('neighborhood_pois', 'Curated POIs with Google Place IDs for map markers'),
    ],
  }),
  tags: [
    'google',
    'maps',
    'places',
    '3d',
    'satellite',
    'neighborhood',
    'pois',
    'nearby',
    'search',
    'markers',
    'walk-score',
    'embed',
  ],
  defaultGridSpan: 12,
};
